import React, { useState } from 'react';
import { History, Trophy, Medal, ChevronDown, ChevronUp, Building2, Calendar, Tag } from 'lucide-react';
import { useTournamentHistory, TournamentHistoryItem } from '@/hooks/useTournamentHistory';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

interface Props {
  userId?: string;
}

const INITIAL_LIMIT = 5;

const resultConfig: Record<TournamentHistoryItem['result'], { color: string }> = {
  champion: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  finalist: { color: 'bg-slate-100 text-slate-700 border-slate-200' },
  sf: { color: 'bg-orange-100 text-orange-700 border-orange-200' },
  qf: { color: 'bg-blue-100 text-blue-700 border-blue-200' },
  participation: { color: 'bg-muted text-muted-foreground border-border' },
};

const formatDisplay = (f: string) => {
  const lower = f.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (lower.includes('eliminacion') || lower.includes('elimination')) return 'Relámpago';
  return f.charAt(0).toUpperCase() + f.slice(1);
};

const TournamentHistory: React.FC<Props> = ({ userId }) => {
  const { history, loading } = useTournamentHistory(userId);
  const { t, language } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const resultLabels: Record<TournamentHistoryItem['result'], string> = {
    champion: t('profile.champion'),
    finalist: t('profile.runnerUp'),
    sf: t('profile.semiFinals'),
    qf: t('profile.quarterFinals'),
    participation: t('profile.participation') || 'Participación',
  };

  if (loading) {
    return (
      <div className="mb-4">
        <Skeleton className="h-6 w-48 mb-3" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!history.length) {
    return (
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 px-1">
          <History className="h-4 w-4" />
          {t('profile.tournamentHistory')}
        </h3>
        <div className="p-6 bg-card rounded-lg border text-center text-sm text-muted-foreground">
          {t('profile.noTournamentHistory')}
        </div>
      </div>
    );
  }

  const displayed = expanded ? history : history.slice(0, INITIAL_LIMIT);

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 px-1">
        <History className="h-4 w-4" />
        {t('profile.tournamentHistory')}
      </h3>

      <div className="space-y-2.5">
        {displayed.map(item => {
          const cfg = resultConfig[item.result];
          const dateStr = (() => {
            try {
              return format(new Date(item.date), 'd MMM yyyy', {
                locale: language === 'es' ? es : enUS,
              });
            } catch {
              return item.date;
            }
          })();

          return (
            <div
              key={item.id}
              className="p-3.5 bg-card rounded-xl border shadow-sm"
            >
              {/* Row 1: Name + Result badge */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="shrink-0">
                    {item.result === 'champion' ? (
                      <Trophy className="h-4.5 w-4.5 text-yellow-500" />
                    ) : item.result === 'finalist' ? (
                      <Medal className="h-4.5 w-4.5 text-slate-400" />
                    ) : (
                      <div className="h-4.5 w-4.5 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-[9px] font-bold text-muted-foreground">
                          {item.result === 'sf' ? 'SF' : item.result === 'qf' ? 'QF' : 'P'}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 shrink-0 ${cfg.color}`}>
                  {resultLabels[item.result]}
                </Badge>
              </div>

              {/* Details */}
              <div className="pl-6.5 space-y-1">
                {item.organizer && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{item.organizer}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Tag className="h-3 w-3 shrink-0" />
                  <span>{item.category} · {item.subcategory}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 shrink-0" />
                  <span>{dateStr} · {item.sport}{item.format ? ` · ${formatDisplay(item.format)}` : ''}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {history.length > INITIAL_LIMIT && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-2 flex items-center justify-center gap-1 py-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? (
            <>
              {t('profile.viewLess')} <ChevronUp className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              {t('organizer.showMore')} ({history.length - INITIAL_LIMIT}) <ChevronDown className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default TournamentHistory;
